import React, { useState, useRef } from 'react';
import { Select } from 'syngenta-digital-cropwise-react-ui-kit';
import { useDataRefresh } from 'screens/Applications&WorkOrders/StepsLayout/Components/DetailsStep/hooks/useDataRefresh';
import { CompanyPeopleItem } from 'components/CompanyPeopleDropdown/types';
import AddNewCompanyPeopleDrawer from 'components/CompanyPeopleDropdown/components/AddNewCompanyPeopleDrawer';
import getDropdownParentWithoutDetaching from 'utilities/getDropdownParentWithoutDetaching';
import ChevronUp from 'assets/chevron_up.svg?react';
import ChevronDown from 'assets/chevron_down.svg?react';
import { PersonCompany } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import filterOptionByNormalizedString from 'screens/Applications&WorkOrders/StepsLayout/Helpers/filterOptions';
import focusInput from 'screens/Applications&WorkOrders/StepsLayout/Helpers/focusInput';
import CreateNewButton from 'components/CreateNewButton';
import PersonOptionLabel from 'screens/Applications&WorkOrders/StepsLayout/Components/DetailsStep/components/PersonOptionLabel';
import alphanumSort from 'helpers/sorters';

interface SelectApplicatorProps {
  value?: PersonCompany | null;
  onChange: (value: PersonCompany | null) => void;
  onBlur?: () => void;
  style?: React.CSSProperties;
  listHeight?: number;
  suffixIcon?: React.ReactNode;
  onDropdownVisibleChange?: (open: boolean) => void;
  title: string;
  people: PersonCompany[];
  companies: PersonCompany[];
  peopleLoading: boolean;
  companiesLoading: boolean;
  loadPeople: () => void;
  loadCompanies: () => void;
  hideCompanies?: boolean;
  hidePeople?: boolean;
  allowClear?: boolean;
  defaultDrawerType?: 'People' | 'Company';
  disableDrawerTypeChange?: boolean;
  id?: string;
}

const SelectApplicator: React.FC<SelectApplicatorProps> = ({
  value,
  onChange,
  onBlur,
  style,
  listHeight,
  suffixIcon,
  onDropdownVisibleChange,
  title,
  people,
  companies,
  peopleLoading,
  companiesLoading,
  loadPeople,
  loadCompanies,
  hideCompanies = false,
  hidePeople = false,
  allowClear = false,
  defaultDrawerType = 'People',
  disableDrawerTypeChange = false,
  id
}) => {
  const [isCreateNewOpen, setIsCreateNewOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { triggerRefresh } = useDataRefresh();

  // Helper function to get display value for the Select
  const getDisplayValue = () => {
    if (!value?.id) return undefined;

    const existsInPeople = people?.some(
      (p: { id: string }) => p.id === value.id
    );
    const existsInCompanies = companies?.some(
      (c: { id: string }) => c.id === value.id
    );

    if (existsInPeople || existsInCompanies) {
      return value.id;
    }

    return value.name ? `temp_${value.id}` : value.id;
  };

  const handleChange = (selectedValue: string, option: any) => {
    if (selectedValue && option) {
      const selectedItem = option.item;
      onChange(selectedItem);
    } else {
      onChange(null);
    }
  };

  const handleClear = () => onChange(null);

  const handleDropdownVisibleChange = (open: boolean) => {
    setIsDropdownOpen(open);
    if (onDropdownVisibleChange) {
      onDropdownVisibleChange(open);
    }
  };

  const handleCreateNew = () => {
    setIsCreateNewOpen(true);
    handleDropdownVisibleChange(false);
  };

  const refocusParentInput = () => {
    focusInput(containerRef, 'input');
  };

  const handleCompanyPeopleAdded = async (
    newValue: CompanyPeopleItem | null
  ) => {
    if (newValue) {
      if (newValue.type === 'People') {
        loadPeople();
        triggerRefresh('create-application-people');
      } else {
        loadCompanies();
        triggerRefresh('create-application-companies');
      }

      setTimeout(() => {
        onChange({
          id: newValue.id,
          name: newValue.name,
          type: newValue.type,
          licenseNumber: newValue.licenseNumber
        });
      }, 100);
    }
    setIsCreateNewOpen(false);
    refocusParentInput();
  };

  const peopleOptions = hidePeople
    ? []
    : alphanumSort([...people], 'name').map((person: PersonCompany) => ({
        value: person.id,
        item: {
          id: person.id,
          name: person.name,
          type: 'People',
          licenseNumber: person.licenseNumber
        },
        children: person.name
      }));

  const companyOptions = hideCompanies
    ? []
    : alphanumSort([...companies], 'name').map((company: PersonCompany) => ({
        value: company.id,
        item: {
          id: company.id,
          name: company.name,
          type: 'Company'
        },
        children: company.name
      }));

  const tempOption =
    value &&
    !people.some(p => p.id === value.id) &&
    !companies.some(c => c.id === value.id) &&
    value.name
      ? {
          value: `temp_${value.id}`,
          item: value,
          children: value.name
        }
      : null;

  const chevronIcon = isDropdownOpen ? (
    <ChevronUp width={18} height={18} />
  ) : (
    <ChevronDown width={18} height={18} />
  );

  const dropdownRender = (menu: React.ReactElement) => (
    <div className="cwf-custom-select-dropdown">
      {menu}
      <CreateNewButton onCreateNew={handleCreateNew} text={title} />
    </div>
  );

  return (
    <div ref={containerRef}>
      <Select
        data-testid={`applicator-select-${id}`}
        style={style}
        showSearch
        loading={peopleLoading || companiesLoading}
        value={getDisplayValue()}
        optionLabelProp="label"
        onChange={handleChange}
        onClear={handleClear}
        onBlur={onBlur}
        allowClear={allowClear}
        className={`custom-select cwf-select-applicator-${id} w-full ${
          value?.type === 'People' ? 'person-input' : 'company-input'
        }`}
        listHeight={listHeight}
        showArrow
        dropdownStyle={{ whiteSpace: 'normal' }}
        suffixIcon={suffixIcon || chevronIcon}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        getPopupContainer={getDropdownParentWithoutDetaching}
        dropdownRender={dropdownRender}
        filterOption={filterOptionByNormalizedString}
      >
        {tempOption && (
          <Select.Option
            key={tempOption.value}
            value={tempOption.value}
            item={tempOption.item}
            label={tempOption.children}
            data-testid={`selector-applicator-temp-${tempOption.value}-option`}
          >
            {tempOption.children}
          </Select.Option>
        )}

        {peopleOptions.length > 0 && (
          <>
            {peopleOptions.map((option: typeof peopleOptions[0]) => (
              <Select.Option
                key={option.value}
                value={option.value}
                item={option.item}
                label={option.item.name}
                data-testid={`selector-applicator-people-${option.children}-option`}
              >
                <PersonOptionLabel
                  name={option.item.name}
                  licenseNumber={option.item.licenseNumber}
                />
              </Select.Option>
            ))}
          </>
        )}

        {companyOptions.length > 0 && (
          <>
            {companyOptions.map((option: typeof companyOptions[0]) => (
              <Select.Option
                key={option.value}
                value={option.value}
                item={option.item}
                label={option.children}
                data-testid={`selector-applicator-companies-${option.children}-option`}
              >
                {option.children}
              </Select.Option>
            ))}
          </>
        )}
      </Select>

      <AddNewCompanyPeopleDrawer
        open={isCreateNewOpen}
        onClose={() => {
          setIsCreateNewOpen(false);
          refocusParentInput();
        }}
        onCompanyPeopleAdded={handleCompanyPeopleAdded}
        title={title}
        defaultType={defaultDrawerType}
        disableTypeChange={disableDrawerTypeChange}
      />
    </div>
  );
};

export default SelectApplicator;
